import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import {
  PaymentMethodSummary,
  getPaymentMethods,
  setDefaultPaymentMethod,
  deletePaymentMethod,
  addBankMandate,
} from "../api/services";

export interface UsePaymentMethodsResult {
  methods: PaymentMethodSummary[];
  loading: boolean;
  error: string | null;
  successMessage: string | null;
  busyId: number | null;
  refresh: () => Promise<void>;
  setDefault: (id: number) => Promise<void>;
  remove: (id: number) => Promise<void>;
  addMandate: (id: number) => Promise<void>;
  flash: (msg: string) => void;
  clearError: () => void;
}

/**
 * Shared state + actions for the user's payment methods.
 * Used by `/payment-methods` and the SDA Financial step so behavior stays
 * consistent across both screens.
 */
export function usePaymentMethods(): UsePaymentMethodsResult {
  const { t } = useTranslation();
  const { refreshUser } = useAuth();
  const [methods, setMethods] = useState<PaymentMethodSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getPaymentMethods();
      setMethods(res.data);
    } catch (err) {
      console.error(err);
      setError(t("paymentMethods.errorLoading"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const flash = useCallback((msg: string) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(null), 4000);
  }, []);

  const setDefault = useCallback(
    async (id: number) => {
      setBusyId(id);
      try {
        await setDefaultPaymentMethod(id);
        flash(t("paymentMethods.successDefault"));
        await refresh();
      } catch (err) {
        console.error(err);
        setError(t("paymentMethods.errorSetDefault"));
      } finally {
        setBusyId(null);
      }
    },
    [flash, refresh, t],
  );

  const remove = useCallback(
    async (id: number) => {
      if (!window.confirm(t("paymentMethods.confirmRemove"))) return;
      setBusyId(id);
      try {
        await deletePaymentMethod(id);
        flash(t("paymentMethods.successRemoved"));
        await refresh();
        // Removing the mandate-less bank may clear the app-wide gate.
        refreshUser();
      } catch (err: any) {
        console.error(err);
        // Surface the backend's message (e.g. the last-bank-account guard)
        // when present; fall back to the generic copy otherwise.
        setError(
          err?.response?.data?.message || t("paymentMethods.errorRemove"),
        );
      } finally {
        setBusyId(null);
      }
    },
    [flash, refresh, refreshUser, t],
  );

  const addMandate = useCallback(
    async (id: number) => {
      setBusyId(id);
      try {
        await addBankMandate(id, true);
        flash(t("paymentMethods.successMandateAdded"));
        await refresh();
        // Authorizing the bank may clear the app-wide gate.
        refreshUser();
      } catch (err: any) {
        console.error(err);
        setError(
          err?.response?.data?.message || t("paymentMethods.errorAddMandate"),
        );
        // Rethrow so the disclosure modal keeps open and can show the error.
        throw err;
      } finally {
        setBusyId(null);
      }
    },
    [flash, refresh, refreshUser, t],
  );

  return {
    methods,
    loading,
    error,
    successMessage,
    busyId,
    refresh,
    setDefault,
    remove,
    addMandate,
    flash,
    clearError: () => setError(null),
  };
}
